import {
  format,
  parseISO,
  parse,
  isValid,
  subDays,
  subWeeks,
  subMonths,
  subYears,
} from "date-fns";

// global date format
export const DATE_FORMAT = "MM/dd/yyyy" as const; // date-fns format
export const DATE_PLACEHOLDER = "MM/DD/YYYY" as const; // display format for users
export const DATE_AND_TIME_FORMAT = "MM/dd/yyyy h:mm a" as const;

// predefined time formats, passed as keys to getTimeFromISOString
const timeFormats = {
  dateTime: `${DATE_FORMAT} h:mm a`,
  date: DATE_FORMAT,
  time: "h:mm a",
} as const;

type TimeKeys = keyof typeof timeFormats;

/**
 * Convert an ISO 8601 date string to a formatted string
 *
 * @param isoString - the ISO 8601 date string (e.g., 2023-10-15T13:45:30Z)
 * @param formatKey - the key for the desired format (default is "dateTime")
 * @returns the formatted date string or a placeholder if input is invalid/null
 */
export const getTimeFromISOString = (
  isoString: string | null,
  formatKey: TimeKeys = "dateTime",
): string => {
  if (isoString === null) {
    return "N/A";
  }

  try {
    const date = parseISO(isoString);
    if (!isValid(date)) {
      console.error(`Invalid ISO date string provided: ${isoString}`);
      return "N/A";
    }

    return format(date, timeFormats[formatKey]);
  } catch (error) {
    console.error(`Error parsing date string: ${isoString}`, error);
    return "N/A";
  }
};

/**
 * Format an ISO datetime string as a date-only value without timezone shifting.
 * Uses the YYYY-MM-DD segment before "T" to preserve calendar day semantics.
 */
export const getDateFromISOStringDatePart = (
  isoString: string | null,
): string => {
  if (!isoString) return "N/A";

  const [datePart] = isoString.split("T");
  const parsedDate = parse(datePart, "yyyy-MM-dd", new Date());

  if (!isValid(parsedDate)) {
    console.error(`Invalid ISO date string provided: ${isoString}`);
    return "N/A";
  }

  return format(parsedDate, DATE_FORMAT);
};

/**
 * Used with the DatePicker component to convert a Date object to a date string
 * @param date - the Date object to convert
 * @param format - the format of the date string (default is DATE_FORMAT)
 * @returns the date string or null if the date is null
 */
export const DATE_TO_STRING = (
  date: Date | null,
  formatString: string = DATE_FORMAT,
): string | null => {
  if (!date) return null;
  return format(date, formatString);
};

/**
 * Used with the DatePicker component to convert a date string to a Date object
 * @param date - the date string to convert
 * @param format - the format of the date string (default is DATE_FORMAT)
 * @returns the Date object or null if the date is null
 */
export const STRING_TO_DATE = (
  date: string | null,
  formatString: string = DATE_FORMAT,
): Date | null => {
  if (!date) return null;

  // Try parsing with the provided format first
  let parsedDate = parse(date, formatString, new Date());

  // If that fails, try with hyphens (MM-dd-yyyy)
  if (!isValid(parsedDate)) {
    parsedDate = parse(date, formatString.replace(/\//g, "-"), new Date());
  }

  // If both fail, try parsing as ISO
  if (!isValid(parsedDate)) {
    parsedDate = parseISO(date);
  }

  return isValid(parsedDate) ? parsedDate : null;
};

/**
 * Comparator for sorting objects by their `id` property.
 * @param a - First object with optional `id`
 * @param b - Second object with optional `id`
 * @returns number for array sort
 */
export const dateComparator = (
  a: { id?: number },
  b: { id?: number },
): number => (a.id && b.id ? a.id - b.id : a.id ? -1 : b.id ? 1 : 0);

// relative date period types
export type RelativeDatePeriod =
  | "past_day"
  | "past_week"
  | "past_month"
  | "past_3_months"
  | "past_year"
  | "all_time";

/**
 * calculate a date in the past based on a relative period
 * @param period - the relative time period
 * @param baseDate - optional base date (defaults to current date/time)
 * @param stabilizeToHour - whether to round down to the start of the hour for stability
 * @returns date object for the calculated past date, or undefined for "all_time"
 */
export const getRelativeDate = (
  period: RelativeDatePeriod,
  baseDate?: Date,
  stabilizeToHour: boolean = true,
): Date | undefined => {
  if (period === "all_time") {
    return undefined;
  }

  // clone baseDate so callers' Date objects aren't mutated by setMinutes below
  const now = baseDate ? new Date(baseDate) : new Date();

  // optionally stabilize to hour boundary to prevent infinite re-renders
  if (stabilizeToHour) {
    now.setMinutes(0, 0, 0);
  }

  switch (period) {
    case "past_day":
      return subDays(now, 1);
    case "past_week":
      return subWeeks(now, 1);
    case "past_month":
      return subMonths(now, 1);
    case "past_3_months":
      return subMonths(now, 3);
    case "past_year":
      return subYears(now, 1);
    default:
      throw new Error(`Unknown relative date period: ${period}`);
  }
};

/**
 * get an ISO string for a relative date period
 * @param period - The relative time period
 * @param baseDate - optional base date (defaults to current date/time)
 * @param stabilizeToHour - whether to round down to the start of the hour for stability
 * @returns ISO string for the calculated date, or undefined for "all_time"
 */
export const getRelativeDateISO = (
  period: RelativeDatePeriod,
  baseDate?: Date,
  stabilizeToHour: boolean = true,
): string | undefined => {
  const date = getRelativeDate(period, baseDate, stabilizeToHour);
  return date?.toISOString();
};

/**
 * common relative date period options
 */
export const RELATIVE_DATE_OPTIONS = [
  { label: "Past day", value: "past_day" as RelativeDatePeriod },
  { label: "Past week", value: "past_week" as RelativeDatePeriod },
  { label: "Past month", value: "past_month" as RelativeDatePeriod },
  { label: "Past 3 months", value: "past_3_months" as RelativeDatePeriod },
  { label: "Past year", value: "past_year" as RelativeDatePeriod },
  { label: "All time", value: "all_time" as RelativeDatePeriod },
] as const;

// quick date range types for reporting filters
export type QuickDateRange =
  | "this_month"
  | "last_month"
  | "this_year"
  | "last_year"
  | "ytd";

/**
 * calculate start and end dates for quick date range filters
 * @param range - the quick date range type
 * @param baseDate - optional base date (defaults to current date)
 * @returns object with start and end dates for the range
 */
export const getQuickDateRange = (
  range: QuickDateRange,
  baseDate?: Date,
): { start: Date; end: Date } => {
  const now = baseDate || new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  switch (range) {
    case "this_month": {
      // first day of current month to last day of current month
      const start = new Date(currentYear, currentMonth, 1);
      const end = new Date(currentYear, currentMonth + 1, 0);
      return { start, end };
    }

    case "last_month": {
      // first day of previous month to last day of previous month
      const start = new Date(currentYear, currentMonth - 1, 1);
      const end = new Date(currentYear, currentMonth, 0);
      return { start, end };
    }

    case "this_year": {
      // january 1 of current year to december 31 of current year
      const start = new Date(currentYear, 0, 1);
      const end = new Date(currentYear, 11, 31);
      return { start, end };
    }

    case "last_year": {
      // january 1 of previous year to december 31 of previous year
      const start = new Date(currentYear - 1, 0, 1);
      const end = new Date(currentYear - 1, 11, 31);
      return { start, end };
    }

    case "ytd": {
      // january 1 of current year to today
      const start = new Date(currentYear, 0, 1);
      const end = new Date(now);
      return { start, end };
    }

    default:
      throw new Error(`Unknown quick date range: ${range}`);
  }
};

/**
 * quick date range options for reporting filters
 */
export const QUICK_DATE_RANGE_OPTIONS = [
  { label: "This Month", value: "this_month" as QuickDateRange },
  { label: "Last Month", value: "last_month" as QuickDateRange },
  { label: "This Year", value: "this_year" as QuickDateRange },
  { label: "Last Year", value: "last_year" as QuickDateRange },
  { label: "YTD", value: "ytd" as QuickDateRange },
] as const;

/**
 * calculate age from a birth date
 * @param birthDate - the birth date as a string or Date object
 * @param referenceDate - optional reference date (defaults to today)
 * @returns the age in years, or null if birth date is invalid
 */
export const calculateAge = (
  birthDate: string | Date | null | undefined,
  referenceDate?: Date,
): number | null => {
  if (!birthDate) return null;

  try {
    const birth =
      typeof birthDate === "string" ? parseISO(birthDate) : birthDate;
    if (!isValid(birth)) return null;

    const reference = referenceDate || new Date();
    let age = reference.getFullYear() - birth.getFullYear();
    const monthDiff = reference.getMonth() - birth.getMonth();

    // adjust age if birthday hasn't occurred yet this year
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && reference.getDate() < birth.getDate())
    ) {
      age--;
    }

    return age >= 0 ? age : null;
  } catch {
    return null;
  }
};

/**
 * format a date with age in parentheses
 * @param birthDate - the birth date as a string or Date object
 * @param formatString - the format for the date (default is DATE_FORMAT)
 * @returns formatted string like "MM/DD/YYYY (Age XX)" or just the date if age can't be calculated
 */
export const formatDateWithAge = (
  birthDate: string | Date | null | undefined,
  formatString: string = DATE_FORMAT,
): string => {
  if (!birthDate) return "";

  try {
    const birth =
      typeof birthDate === "string" ? parseISO(birthDate) : birthDate;
    if (!isValid(birth)) return "";

    const formattedDate = format(birth, formatString);
    const age = calculateAge(birth);

    return age !== null ? `${formattedDate} (Age ${age})` : formattedDate;
  } catch {
    return "";
  }
};

/**
 * formats a duration in minutes to a human-readable string
 * @param minutes - the duration in minutes
 * @returns a formatted string like "2 hours 30 minutes" or "<1 minute"
 */
export const formatMinutes = (minutes: number): string => {
  if (minutes < 1) {
    return "<1 minute";
  }

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = Math.round(minutes % 60);

  if (hours > 0) {
    const hourStr = `${hours} hour${hours !== 1 ? "s" : ""}`;
    if (remainingMinutes > 0) {
      return `${hourStr} ${remainingMinutes} minute${remainingMinutes !== 1 ? "s" : ""}`;
    }
    return hourStr;
  }

  return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
};
